import React, { useState, useRef, useEffect, useMemo } from 'react';
import { 
  Box, Typography, MenuItem, Select, FormControl, 
  IconButton, Tooltip, Card, useTheme, Divider, Alert, Avatar, Menu
} from '@mui/material';
import api from '../api/axios';
import ChatMessageList from './ChatMessageList';
import ChatInput from './ChatInput';
import { SmartToy, VolumeUp, VolumeOff, WifiOff, MoreVert, DeleteSweep } from '@mui/icons-material';
import { useNetwork } from '../context/NetworkContext';
import { useAuth } from '../context/AuthContext';

const API_URL = '/ai/chat';
const STORAGE_KEY = 'soil_testing_ai_chat';

// Worker-only direct responses for common, data-backed questions.
// This avoids slow AI coordinator calls and prevents hallucinations.
async function handleWorkerDirectQuery(input, api) {
  const lower = input.toLowerCase();

  if (lower.match(/next job|my job|today.?s job|what.?s my job|job for me|upcoming job/)) {
    try {
      const res = await api.get('/admin/my-jobs');
      const jobs = res.data || [];
      const today = new Date().toISOString().split('T')[0];
      const todaysJobs = jobs
        .filter(j => {
          const d = j.scheduled_date ? j.scheduled_date.split('T')[0] : null;
          return d === today;
        })
        .sort((a, b) => (a.scheduled_date || '').localeCompare(b.scheduled_date || ''));

      if (todaysJobs.length === 0) {
        return "You have no jobs scheduled for today. Check the Schedule tab for upcoming jobs.";
      }
      const next = todaysJobs[0];
      return `Your next job today is for ${next.client_name || 'Client'} at ${next.location || 'TBC'}.`;
    } catch (err) {
      console.error('Failed to load worker jobs:', err);
      return "I couldn't load your jobs right now. Tap the Schedule tab to view them.";
    }
  }

  if (lower.match(/contact the client|message the client|text the client|email the client|call the client|how.*contact.*client|how.*message.*client/)) {
    return "I can't send emails or make phone calls. I can help you message the manager, or (if enabled) draft an SMS to the client for manager approval.";
  }

  if (lower.match(/my schedule|schedule today|this week|jobs this week|week.?s jobs/)) {
    try {
      const res = await api.get('/admin/my-jobs');
      const jobs = res.data || [];
      if (jobs.length === 0) return "You have no scheduled jobs right now.";
      const lines = jobs
        .sort((a, b) => (a.scheduled_date || '').localeCompare(b.scheduled_date || '') || (a.scheduled_time || '99:99:99').localeCompare(b.scheduled_time || '99:99:99'))
        .slice(0, 5)
        .map(j => `${j.scheduled_date || 'TBD'} ${j.scheduled_time?.slice(0, 5) || ''} — ${j.client_name || 'Client'} at ${j.location || 'TBC'}`);
      return `Your upcoming jobs:\n${lines.join('\n')}`;
    } catch (err) {
      return "I couldn't load your schedule. Tap the Schedule tab.";
    }
  }

  if (lower.match(/message manager|text manager|tell manager|notify manager|escalate/)) {
    return "Tap the Messages tab, then 'Message Manager' to send a direct message.";
  }

  if (lower.match(/locked|schedule change|change the schedule|reschedule|move my job|swap jobs|shift my job|can't change|how.*change.*schedule|how.*reschedule/)) {
    return "Workers can't change locked schedules from this app. If you need to reschedule a job, swap the order, or change a time, tap the Messages tab and 'Message Manager' to request the change.";
  }

  if (lower.match(/route change|change route|request a route change|reroute|different route/)) {
    return "Workers can't change routes from this app. If you need a route change, tap the Messages tab and 'Message Manager' to request it.";
  }

  if (lower.match(/confirmed|all confirmed|clients confirmed|did you message|did they confirm|confirmation status|are they confirmed/)) {
    return "I can't check client confirmation status here. Tap the Messages tab to see updates from the manager, or 'Message Manager' to ask.";
  }

  return null;
}

const Chatbot = ({ embedded = false }) => {
  const theme = useTheme();
  const { isOnline } = useNetwork();
  const { user } = useAuth();
  
  const canManageAI = user?.role === 'admin' || user?.role === 'manager';
  const isWorker = user?.role === 'worker';
  const userStorageKey = user ? `${STORAGE_KEY}_${user.id}` : STORAGE_KEY;
  
  // Load initial state from localStorage
  const loadStoredData = () => {
    try {
      const stored = localStorage.getItem(userStorageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to load stored chat data', e);
    }
    return null;
  };

  const storedData = useMemo(loadStoredData, [userStorageKey]);

  const [messages, setMessages] = useState(storedData?.messages || [
    { sender: 'assistant', text: isWorker ? "Hi! I'm your AI assistant. Ask me to message a client, check your schedule, or request a route change." : "G'day Tim. I'm Ash — what do you need?" }
  ]);
  const [role, setRole] = useState(storedData?.role || (isWorker ? 'worker' : (canManageAI ? 'manager' : 'user'))); 
  const [voiceEnabled, setVoiceEnabled] = useState(storedData?.voiceEnabled || false);
  const [input, setInput] = useState(storedData?.inputDraft || '');

  // Restore conversation from the server when localStorage is empty —
  // the chat is persisted in InternalMessage, so a fresh device or
  // cleared storage shouldn't look like a reset.
  useEffect(() => {
    const hasStored = !!(storedData?.messages?.length);
    if (user && !hasStored) {
      api.get('/ai/chat/history')
        .then(res => {
          const msgs = (res.data?.messages || []).map(m => ({ sender: m.role === 'user' ? 'user' : 'assistant', text: m.text }));
          if (msgs.length) setMessages(msgs);
        })
        .catch(() => { /* offline or no history — keep the greeting */ });
    }
  }, [user, storedData]);

  // Save to localStorage whenever state changes
  useEffect(() => {
    if (!user) return;
    try {
      const dataToStore = {
        messages,
        role,
        voiceEnabled,
        inputDraft: input,
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem(userStorageKey, JSON.stringify(dataToStore));
    } catch (e) {
      console.error('Failed to save chat data', e);
    }
  }, [messages, role, voiceEnabled, input, userStorageKey]);

  // Sync with other instances (tabs or components) via Storage event
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === userStorageKey && e.newValue) {
        try {
          const newData = JSON.parse(e.newValue);
          // Only update if data is different to avoid loops
          setMessages(prev => JSON.stringify(prev) !== JSON.stringify(newData.messages) ? newData.messages : prev);
          setRole(prev => prev !== newData.role ? newData.role : prev);
          setVoiceEnabled(prev => prev !== newData.voiceEnabled ? newData.voiceEnabled : prev);
          // Don't sync input draft across tabs as it would be confusing
        } catch (err) {
          console.error('Failed to sync storage change', err);
        }
      } else if (e.key === userStorageKey && !e.newValue) {
        // Data was cleared
        setMessages([{ sender: 'assistant', text: 'Hello! I am your Soil Testing Assistant. How can I help you today?' }]);
        setInput('');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [userStorageKey]);

  // Update role if user permission changes (e.g. on load)
  useEffect(() => {
    if (user && isWorker) {
        setRole('worker');
    } else if (user && !canManageAI) {
        setRole('user');
    } else if (user && canManageAI && role === 'user' && messages.length === 1) {
        // Only auto-switch to manager if it's the start of convo, otherwise keep what they selected
        setRole('manager');
    }
  }, [user, canManageAI, isWorker]);

  const [listening, setListening] = useState(false);
  const [loading, setLoading] = useState(false);
  const recognitionRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClearChat = () => {
    const initialMessage = { sender: 'assistant', text: 'Hello! I am your Soil Testing Assistant. How can I help you today?' };
    setMessages([initialMessage]);
    localStorage.removeItem(userStorageKey);
    handleMenuClose();
  };

  // Voice output
  const speak = (text) => {
    if (!voiceEnabled) return;
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utter = new window.SpeechSynthesisUtterance(text);
      // Try to find a good English voice
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(v => v.lang.includes('en-AU')) || 
                             voices.find(v => v.lang.includes('en-GB')) || 
                             voices.find(v => v.lang.startsWith('en'));
      
      if (preferredVoice) utter.voice = preferredVoice;
      utter.rate = 1.0;
      utter.pitch = 1.0;
      
      window.speechSynthesis.speak(utter);
    }
  };

  // Voice recognition setup
  const startListening = () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      alert('Your browser does not support speech recognition.');
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-AU';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
      setListening(false);
    };
    
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    
    recognition.start();
    setListening(true);
    recognitionRef.current = recognition;
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setListening(false);
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    const userMsg = { role: 'user', content: input };
    const currentInput = input.trim();
    setMessages(msgs => [...msgs, { sender: 'user', text: currentInput }]);

    // Prepare history for API (excluding the current message we're about to send)
    // Map internal message format to API format {role, content}
    const apiHistory = messages.slice(1).map(msg => ({
      role: msg.sender === 'assistant' ? 'assistant' : 'user',
      content: msg.text
    }));

    setInput('');
    setLoading(true);
    
    try {
      const response = await api.post(
        API_URL,
        { 
          message: input, 
          context: {}, 
          role,
          history: apiHistory
        },
        { timeout: 15000 }
      );
      const aiMsg = { sender: 'assistant', text: response.data.response };
      setMessages(msgs => [...msgs, aiMsg]);
      speak(response.data.response);
    } catch (err) {
      let errorMsg = 'Sorry, there was an error.';
      if (err.code === 'ECONNABORTED') {
        errorMsg = 'The server took too long to respond. Please try again.';
      } else if (err.response && err.response.data && err.response.data.detail) {
        errorMsg = err.response.data.detail;
      }
      const errResponse = { sender: 'assistant', text: errorMsg };
      setMessages(msgs => [...msgs, errResponse]);
      speak(errorMsg);
    }
    setLoading(false);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  return (
    <Card 
      elevation={0} 
      sx={{ 
        height: embedded ? '100%' : 'calc(100vh - 140px)', 
        display: 'flex', 
        flexDirection: 'column',
        border: embedded ? 'none' : '1px solid #E2E8F0',
        borderRadius: embedded ? 0 : 3,
        overflow: 'hidden',
        bgcolor: 'white',
        boxShadow: embedded ? 'none' : '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          p: 2, 
          borderBottom: '1px solid #F1F5F9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'white'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box 
            sx={{ 
              width: 48, 
              height: 48, 
              borderRadius: '16px', 
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)'
            }}
          >
            <SmartToy />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={800} lineHeight={1.2}>
              AI Assistant
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.5 }}>
                <Box 
                    sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: isOnline ? '#22C55E' : '#EF4444',
                        boxShadow: isOnline ? '0 0 0 2px rgba(34, 197, 94, 0.2)' : 'none'
                    }} 
                />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {isOnline ? 'Online' : 'Offline'}
                </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 120, display: { xs: 'none', sm: 'block' }, visibility: isWorker ? 'hidden' : 'visible' }}>
              <Select
                value={role}
                onChange={e => setRole(e.target.value)}
                variant="outlined"
                disabled={!isOnline || isWorker}
                sx={{ 
                  height: 36,
                  borderRadius: 2,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  bgcolor: '#F8FAFC',
                  '& fieldset': { borderColor: '#E2E8F0' },
                  '&:hover fieldset': { borderColor: '#CBD5E1' }
                }}
              >
                <MenuItem value="manager">Manager View</MenuItem>
                <MenuItem value="user">Client View</MenuItem>
              </Select>
            </FormControl>
            
            <Tooltip title={voiceEnabled ? "Mute Voice" : "Enable Voice"}>
                <IconButton 
                    onClick={() => setVoiceEnabled(!voiceEnabled)}
                    color={voiceEnabled ? "primary" : "default"}
                    size="small"
                    disabled={!isOnline}
                    sx={{ 
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        bgcolor: voiceEnabled ? 'primary.50' : '#F8FAFC',
                        border: '1px solid',
                        borderColor: voiceEnabled ? 'primary.100' : '#E2E8F0',
                        '&:hover': { bgcolor: voiceEnabled ? 'primary.100' : '#F1F5F9' }
                    }}
                >
                    {voiceEnabled ? <VolumeUp fontSize="small" /> : <VolumeOff fontSize="small" />}
                </IconButton>
            </Tooltip>

            <IconButton 
                onClick={handleMenuOpen}
                size="small"
                sx={{ 
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    bgcolor: '#F8FAFC',
                    border: '1px solid #E2E8F0'
                }}
            >
                <MoreVert fontSize="small" />
            </IconButton>
            
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={handleClearChat} sx={{ color: 'error.main', gap: 1 }}>
                    <DeleteSweep fontSize="small" />
                    <Typography variant="body2" fontWeight={600}>Clear Conversation</Typography>
                </MenuItem>
            </Menu>
        </Box>
      </Box>

      {/* Chat Area */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', bgcolor: '#F8FAFC' }}>
        <ChatMessageList messages={messages} loading={loading} />
        {!isOnline && (
            <Box sx={{ p: 2, pb: 0 }}>
                <Alert severity="warning" icon={<WifiOff fontSize="inherit" />} sx={{ borderRadius: 2 }}>
                    AI Chat is unavailable while offline.
                </Alert>
            </Box>
        )}
      </Box>

      {/* Input Area */}
      <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #F1F5F9' }}>
        <ChatInput
          input={input}
          setInput={setInput}
          onSend={handleSend}
          listening={listening}
          onMicClick={listening ? stopListening : startListening}
          loading={loading || !isOnline}
          onInputKeyDown={handleInputKeyDown}
        />
      </Box>
    </Card>
  );
};

export default Chatbot;
